import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, X, Calendar, Clock, MapPin, Users, Euro, Wand2, Send, ArrowLeft, MessageCircle, ChevronRight } from "lucide-react";

const EVENT_TYPES = [
  { key: "matrimonio", label: "Matrimonio", icon: "💍" },
  { key: "addio_celibato", label: "Addio celibato", icon: "🥃" },
  { key: "addio_nubilato", label: "Addio nubilato", icon: "👯" },
  { key: "laurea", label: "Laurea", icon: "🎓" },
  { key: "compleanno", label: "Compleanno", icon: "🎂" },
  { key: "battesimo", label: "Battesimo", icon: "👶" },
  { key: "cresima", label: "Cresima", icon: "✝️" },
  { key: "comunione", label: "Comunione", icon: "🕊️" },
  { key: "fidanzamento", label: "Fidanzamento", icon: "💞" },
  { key: "cenone_capodanno", label: "Cenone Capodanno", icon: "🎆" },
  { key: "pranzo_natale", label: "Pranzo Natale", icon: "🎄" },
  { key: "pranzo_pasqua", label: "Pranzo Pasqua", icon: "🐣" },
  { key: "pranzo_pasquetta", label: "Pranzo Pasquetta", icon: "🧺" },
  { key: "pranzo_ferragosto", label: "Pranzo Ferragosto", icon: "🏖️" },
  { key: "cena_ferragosto", label: "Cena Ferragosto", icon: "🌅" },
  { key: "altro", label: "Altro", icon: "✨" },
];

export default function MagicEventButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="btn-magic-event"
        className="relative group inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-white shadow-lg shadow-purple-500/30 bg-gradient-to-r from-purple-600 via-fuchsia-500 to-orange-500 hover:shadow-purple-500/50 transition-all"
        title="Genera un evento completo con AI"
      >
        <Wand2 className="w-4 h-4" />
        <span className="hidden sm:inline">Magic Event</span>
        <span className="absolute -top-1 -right-1 inline-flex w-2.5 h-2.5 rounded-full bg-yellow-300 animate-ping" />
      </button>
      {open && <MagicEventModal onClose={() => setOpen(false)} />}
    </>
  );
}

function formatApplied(a) {
  if (!a) return "";
  switch (a.op) {
    case "add_dish": return `Piatto aggiunto: ${a.dish_name}`;
    case "remove_dish": return `Piatto rimosso: ${a.dish_name}`;
    case "add_artist": return `Artista aggiunto: ${a.artist_name}`;
    case "remove_artist": return `Artista rimosso: ${a.artist_name}`;
    case "set_drink": return `Bevanda aggiornata: ${a.label || a.drink_type}`;
    case "set_cake": return a.tier === "disabled" ? "Torta rimossa" : `Torta impostata (${a.tier}, €${a.price})`;
    case "add_decoration": return `Decorazione aggiunta: ${a.name} (€${a.total?.toFixed(0)})`;
    case "set_markup": return `Ricarico ${a.category} → ${a.pct}%`;
    default: return a.op;
  }
}

function MagicEventModal({ onClose }) {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const [phase, setPhase] = useState("form"); // form | chat | generating
  const [form, setForm] = useState({
    client_name: "",
    event_type: "matrimonio",
    event_date: today,
    start_time: "19:00",
    end_time: "23:00",
    location_address: "",
    guests: 50,
    service_style: "servito",
    budget_eur: 0,
    drinks_included: true,
    event_vision: "",
    special_requests: "",
  });

  // ---- Chat state ----
  const [messages, setMessages] = useState([]); // {role:'assistant'|'user', text}
  const [chatInput, setChatInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [qaSummary, setQaSummary] = useState(null); // {cocktail, bambini, ...}
  const [progress, setProgress] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  // ---- Step 1: validate form and move to chat ----
  const goToChat = async (e) => {
    e?.preventDefault();
    if (!form.event_date) return toast.error("Inserisci la data dell'evento");
    if (Number(form.guests) < 1) return toast.error("Inserisci almeno 1 ospite");
    setPhase("chat");
    // Send empty user_message to get first AI question
    await sendChatMessage("", []);
  };

  // ---- Chat: send message ----
  const sendChatMessage = async (userMsg, currentHistory) => {
    setThinking(true);
    const history = currentHistory ?? messages;
    try {
      const r = await api.post("/events/magic/qa", {
        base_form: { ...form, guests: Number(form.guests), budget_eur: Number(form.budget_eur) },
        history: history.map((m) => ({ role: m.role, text: m.text })),
        user_message: userMsg,
      });
      const d = r.data;
      if (d.type === "ready") {
        setQaSummary(d.summary || {});
        setMessages((prev) => [...prev, ...(userMsg ? [{ role: "user", text: userMsg }] : []), { role: "assistant", text: d.message || "Pronto a generare!", final: true }]);
      } else {
        setMessages((prev) => [...prev, ...(userMsg ? [{ role: "user", text: userMsg }] : []), { role: "assistant", text: d.text || "..." }]);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Errore AI: ${msg}`);
    } finally {
      setThinking(false);
    }
  };

  const handleSendChat = async (e) => {
    e?.preventDefault();
    const txt = chatInput.trim();
    if (!txt || thinking) return;
    setChatInput("");
    await sendChatMessage(txt, messages);
  };

  const skipToGenerate = async () => {
    // User wants to skip remaining questions
    if (thinking) return;
    setChatInput("");
    await sendChatMessage("Procedi pure con la generazione, ho detto tutto quello che mi serve.", messages);
  };

  // ---- Phase 3: Generate event ----
  const [generatedEvent, setGeneratedEvent] = useState(null); // {id, theme, dishes, etc}
  const [refineMessages, setRefineMessages] = useState([]); // {role, text, applied?}
  const [refineInput, setRefineInput] = useState("");
  const [refining, setRefining] = useState(false);

  const generateEvent = async () => {
    setPhase("generating");
    setProgress("L'AI sta analizzando i tuoi requisiti...");
    setTimeout(() => setProgress("Selezione piatti dal database..."), 1500);
    setTimeout(() => setProgress("Suggerimento artisti e fornitori..."), 4500);
    setTimeout(() => setProgress("Costruzione scaletta evento e bevande..."), 7500);
    setTimeout(() => setProgress("Calcolo food cost e ricarichi..."), 12000);
    try {
      const r = await api.post("/events/magic", {
        ...form,
        guests: Number(form.guests),
        budget_eur: Number(form.budget_eur),
        qa_answers: qaSummary || {},
      });
      const data = r.data;
      // Fetch full event to show summary in refine phase
      const evResp = await api.get(`/events/${data.event_id}`);
      setGeneratedEvent({ ...evResp.data, _meta: data });
      setRefineMessages([
        { role: "assistant", text: `Fatto! Ho generato "${data.theme}" — ${data.dishes_count} piatti, ${data.artists_count} artisti, ${data.timeline_count} momenti. Vuoi aggiungere o modificare qualcosa? (es. "aggiungi una band live", "togli il prosecco", "torta a 3 piani")` },
      ]);
      setPhase("refine");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Errore: ${msg}`);
      setPhase("chat");
    }
  };

  // ---- Phase 4: Refine ----
  const sendRefine = async (e) => {
    e?.preventDefault();
    const txt = refineInput.trim();
    if (!txt || refining || !generatedEvent) return;
    setRefineInput("");
    setRefining(true);
    setRefineMessages((prev) => [...prev, { role: "user", text: txt }]);
    try {
      const r = await api.post(`/events/${generatedEvent.id}/magic/refine`, { message: txt });
      const data = r.data;
      // Refetch full event to refresh summary
      const evResp = await api.get(`/events/${generatedEvent.id}`);
      setGeneratedEvent({ ...evResp.data, _meta: generatedEvent._meta });
      const appliedTxt = (data.applied || []).length > 0
        ? "Modifiche applicate:\n• " + (data.applied || []).map((a) => formatApplied(a)).join("\n• ")
        : "Nessuna modifica applicata.";
      setRefineMessages((prev) => [...prev, { role: "assistant", text: `${data.message}\n\n${appliedTxt}`, applied: data.applied }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      setRefineMessages((prev) => [...prev, { role: "assistant", text: `Errore: ${msg}` }]);
    } finally {
      setRefining(false);
    }
  };

  const finishAndOpen = () => {
    if (!generatedEvent) return;
    onClose();
    navigate(`/eventi/${generatedEvent.id}`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto p-4 sm:p-8" onClick={onClose} data-testid="magic-event-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl mx-auto my-auto shadow-2xl animate-in fade-in slide-in-from-top-4 duration-300 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="relative px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-purple-50 via-fuchsia-50 to-orange-50 rounded-t-2xl flex-shrink-0">
          <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 via-fuchsia-500 to-orange-400 flex items-center justify-center shadow-lg">
              {phase === "chat" ? <MessageCircle className="w-6 h-6 text-white" /> : <Sparkles className="w-6 h-6 text-white" />}
            </div>
            <div className="flex-1">
              <h3 className="font-display text-2xl text-[#0F2741]">
                {phase === "form" && "Magic Event"}
                {phase === "chat" && "Chiacchieriamo un attimo"}
                {phase === "generating" && "Sto generando..."}
                {phase === "refine" && "Affina il tuo evento"}
              </h3>
              <p className="text-xs text-slate-500">
                {phase === "form" && "Inserisci i dati base, poi l'AI ti farà qualche domanda mirata"}
                {phase === "chat" && "L'AI vuole capire meglio per personalizzare tutto"}
                {phase === "generating" && "Costruisco menù, scaletta, bevande e preventivo..."}
                {phase === "refine" && "Modifica menù, artisti, bevande in linguaggio naturale"}
              </p>
            </div>
            {phase === "chat" && (
              <button type="button" onClick={() => setPhase("form")} className="text-xs text-slate-500 hover:text-purple-700 flex items-center gap-1" data-testid="magic-back-to-form">
                <ArrowLeft className="w-3 h-3" /> Modifica dati
              </button>
            )}
          </div>
        </div>

        {/* FORM PHASE */}
        {phase === "form" && (
          <form onSubmit={goToChat} className="contents">
            <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nome cliente</label>
                <input data-testid="magic-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Es. Sig. Rossi" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200" />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5 block">Tipo evento</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-44 overflow-y-auto pr-1">
                  {EVENT_TYPES.map((t) => (
                    <button
                      key={t.key} type="button"
                      onClick={() => setForm({ ...form, event_type: t.key })}
                      data-testid={`magic-type-${t.key}`}
                      className={`px-2 py-2.5 rounded-lg border text-xs font-medium flex flex-col items-center gap-1 ${form.event_type === t.key ? "border-purple-500 bg-purple-50 text-purple-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    >
                      <span className="text-lg">{t.icon}</span>{t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Calendar className="w-3 h-3" /> Data</label>
                  <input data-testid="magic-date" type="date" required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Clock className="w-3 h-3" /> Inizio</label>
                  <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Clock className="w-3 h-3" /> Fine</label>
                  <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" /> Location / Indirizzo</label>
                <input data-testid="magic-location" value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value })} placeholder="Es. Villa Borghese, Via Roma 12, Milano" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Users className="w-3 h-3" /> Ospiti</label>
                  <input data-testid="magic-guests" type="number" min="1" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-right font-semibold" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Euro className="w-3 h-3" /> Budget cliente (€)</label>
                  <input data-testid="magic-budget" type="number" min="0" value={form.budget_eur} onChange={(e) => setForm({ ...form, budget_eur: e.target.value })} placeholder="0 = non specificato" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-right font-semibold" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5 block">Stile di servizio</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {[
                    { k: "servito", label: "Servizio al tavolo", icon: "🍽️" },
                    { k: "buffet", label: "Buffet", icon: "🥗" },
                    { k: "misto", label: "Misto", icon: "✨" },
                    { k: "solo_aperitivo", label: "Solo aperitivo", icon: "🥂" },
                    { k: "solo_rinfresco", label: "Solo rinfresco", icon: "🍰" },
                  ].map((s) => (
                    <button key={s.k} type="button" onClick={() => setForm({ ...form, service_style: s.k })} className={`px-2 py-2 rounded-lg border text-xs flex flex-col items-center gap-1 ${form.service_style === s.k ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`magic-style-${s.k}`}>
                      <span className="text-lg">{s.icon}</span>
                      <span className="font-semibold text-[#0F2741] leading-tight">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.drinks_included} onChange={(e) => setForm({ ...form, drinks_included: e.target.checked })} className="w-4 h-4" data-testid="magic-drinks" />
                <span className="text-sm text-slate-600">Includi bevande nel pacchetto</span>
              </label>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Wand2 className="w-3 h-3" /> Descrivi come vuoi l'evento</label>
                <textarea
                  data-testid="magic-vision"
                  value={form.event_vision}
                  onChange={(e) => setForm({ ...form, event_vision: e.target.value })}
                  placeholder="Es. Vorrei un'atmosfera elegante e romantica ispirata alla Toscana, con luci soffuse, fiori bianchi e una lunga tavolata unica. Musica acustica all'arrivo, poi dj sul tardi. Cucina di tradizione con qualche tocco moderno..."
                  rows="4"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-purple-200 bg-purple-50/30 text-sm focus:outline-none focus:border-purple-500"
                />
                <p className="text-[10px] text-slate-400 mt-1">Racconta liberamente la tua visione. L'AI userà questo per personalizzare tutto e farti domande mirate.</p>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Richieste particolari</label>
                <textarea
                  data-testid="magic-requests"
                  value={form.special_requests}
                  onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                  placeholder="Es. fotografo, dj, mago per bambini, menu vegetariano, intolleranze al glutine, torta dei tre piani, droni per riprese aeree..."
                  rows="2"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex justify-between items-center flex-shrink-0">
              <span className="text-xs text-slate-500">⚡ Generato da Claude Sonnet 4.5</span>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
                <button type="submit" data-testid="btn-next-to-chat" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-600 via-fuchsia-500 to-orange-500 shadow-lg shadow-purple-500/30">
                  Continua con AI <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </form>
        )}

        {/* CHAT PHASE */}
        {phase === "chat" && (
          <>
            <div className="px-6 py-5 overflow-y-auto flex-1 space-y-3 bg-slate-50/40" data-testid="magic-chat-area">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-purple-600 text-white rounded-br-md" : m.final ? "bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-emerald-900 rounded-bl-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {thinking && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 px-4 py-2.5 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                  </div>
                </div>
              )}
              {qaSummary && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2 mt-3" data-testid="magic-qa-summary">
                  <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">Riepilogo raccolto</div>
                  {Object.entries(qaSummary).filter(([_, v]) => v && v !== "—" && v !== "...").map(([k, v]) => (
                    <div key={k} className="text-xs text-emerald-800 flex gap-2">
                      <span className="font-semibold capitalize min-w-[100px]">{k}:</span>
                      <span className="flex-1">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-white rounded-b-2xl flex-shrink-0 space-y-3">
              {!qaSummary ? (
                <form onSubmit={handleSendChat} className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Scrivi la tua risposta..."
                    className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-purple-500"
                    disabled={thinking}
                    data-testid="magic-chat-input"
                    autoFocus
                  />
                  <button type="button" onClick={skipToGenerate} disabled={thinking} className="px-3 py-2.5 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50" data-testid="btn-skip-questions">
                    Salta
                  </button>
                  <button type="submit" disabled={thinking || !chatInput.trim()} className="px-4 py-2.5 rounded-lg font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid="btn-send-chat">
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <button onClick={generateEvent} data-testid="btn-generate-from-chat" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-600 via-fuchsia-500 to-orange-500 shadow-lg shadow-purple-500/30">
                  <Wand2 className="w-5 h-5" /> Genera Magic Event ora
                </button>
              )}
            </div>
          </>
        )}

        {/* GENERATING PHASE */}
        {phase === "generating" && (
          <div className="px-6 py-16 flex flex-col items-center justify-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 via-fuchsia-500 to-orange-400 flex items-center justify-center shadow-2xl shadow-purple-500/40 animate-pulse">
              <Wand2 className="w-10 h-10 text-white" />
            </div>
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            <p className="text-sm text-slate-700 text-center max-w-sm" data-testid="magic-progress">{progress}</p>
          </div>
        )}

        {/* REFINE PHASE */}
        {phase === "refine" && generatedEvent && (
          <RefinePanel
            ev={generatedEvent}
            messages={refineMessages}
            input={refineInput}
            setInput={setRefineInput}
            onSend={sendRefine}
            refining={refining}
            onFinish={finishAndOpen}
          />
        )}
      </div>
    </div>
  );
}


function RefinePanel({ ev, messages, input, setInput, onSend, refining, onFinish }) {
  const chatEndRef = useRef(null);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, refining]);

  const finalPrice = Number(ev.final_price || 0);
  const totalCost = Number(ev.total_cost || 0);
  const budget = Number(ev.magic_budget_target || 0);
  const overBudget = budget > 0 && finalPrice > budget * 1.05;
  const overPct = budget > 0 ? Math.round(((finalPrice - budget) / budget) * 100) : 0;

  const allDishIds = Object.values(ev.dishes || {}).flat();
  const dishCount = allDishIds.length;
  const artistsCount = (ev.artists || []).length;
  const cakeOn = !!ev.cake?.enabled;
  const decoCount = (ev.decorations?.items || []).length;

  return (
    <>
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 flex-shrink-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tema</div>
            <div className="text-sm font-bold text-[#0F2741] truncate" title={ev.magic_theme}>{ev.magic_theme || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Costo / Prezzo</div>
            <div className="text-sm font-bold text-[#0F2741]">€{totalCost.toFixed(0)} → €{finalPrice.toFixed(0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Budget</div>
            {budget > 0 ? (
              <div className={`text-sm font-bold inline-flex items-center gap-1 ${overBudget ? "text-rose-600" : "text-emerald-700"}`} data-testid="refine-budget-status">
                €{budget.toFixed(0)} {overBudget && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 border border-rose-200">+{overPct}%</span>}
              </div>
            ) : <div className="text-sm text-slate-400">—</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Contenuti</div>
            <div className="text-xs text-slate-700 font-semibold">{dishCount} piatti · {artistsCount} artisti{cakeOn ? " · 🎂" : ""}{decoCount ? ` · ${decoCount} addobbi` : ""}</div>
          </div>
        </div>
      </div>

      {/* Voce per voce — alert rossi */}
      <div className="px-6 py-3 border-b border-slate-100 bg-white flex-shrink-0 overflow-x-auto">
        <div className="flex gap-2 min-w-min">
          {[
            { key: "food", label: "Cibo", v: ev.price_breakdown?.food },
            { key: "drinks", label: "Bevande", v: ev.price_breakdown?.drinks },
            { key: "staff", label: "Staff", v: ev.price_breakdown?.staff },
            { key: "artists", label: "Artisti", v: ev.price_breakdown?.artists },
            { key: "photographers", label: "Foto/Drone", v: ev.price_breakdown?.photographers },
            { key: "cake", label: "Torta", v: ev.price_breakdown?.cake },
            { key: "decorations", label: "Addobbi", v: ev.price_breakdown?.decorations },
          ].filter((x) => Number(x.v || 0) > 0).map((x) => {
            // Red if this single category > 50% of budget
            const cat_over = budget > 0 && Number(x.v) > budget * 0.5;
            return (
              <div key={x.key} className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap ${cat_over ? "bg-rose-50 border border-rose-300 text-rose-900" : "bg-slate-50 border border-slate-200 text-slate-700"}`} data-testid={`refine-cat-${x.key}`}>
                <span className="font-semibold">{x.label}:</span> €{Number(x.v).toFixed(0)}
                {cat_over && <span className="ml-1 text-rose-600 font-bold">⚠</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Chat refine */}
      <div className="px-6 py-5 overflow-y-auto flex-1 space-y-3 bg-slate-50/40" data-testid="refine-chat-area">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-purple-600 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {refining && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 px-4 py-2.5 rounded-2xl rounded-bl-md">
              <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Quick suggestions + input */}
      <div className="px-6 py-3 border-t border-slate-100 bg-white flex-shrink-0">
        <div className="flex gap-1.5 flex-wrap mb-2">
          {["Aggiungi torta a 3 piani", "Aggiungi una band live", "Togli il champagne", "Più antipasti", "Cambia primo"].map((s) => (
            <button key={s} type="button" onClick={() => setInput(s)} className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-purple-100 text-xs text-slate-700 border border-slate-200">
              {s}
            </button>
          ))}
        </div>
        <form onSubmit={onSend} className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Es: aggiungi una band live, togli il prosecco, torta cioccolato a piani..."
            className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-purple-500"
            disabled={refining}
            data-testid="refine-input"
          />
          <button type="submit" disabled={refining || !input.trim()} className="px-4 py-2.5 rounded-lg font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid="btn-refine-send">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>

      <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex justify-between items-center flex-shrink-0">
        <span className="text-xs text-slate-500">Chiudi quando hai finito — tutti i dettagli sono già nel preventivo</span>
        <button onClick={onFinish} data-testid="btn-finish-magic" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50">
          Vai al preventivo <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}
